// LOOP-BREAKER (founder ask 2026-10-30): find a run that loops and stop it before it burns tokens.
// Platform-wide, every company, self-hosted (bridge) and managed runs alike.
//
//   POST runs/{id}/turns                 the run's token (a bridge, a runtime): what the agent did
//                                        turn by turn — the tool, a hash of its args and result,
//                                        the assistant's intent, read-only or a write, tokens —
//                                        or that the runtime's own guard stopped it as a loop
//                                        (`guard_stop`). Answers the verdict: ok, warn (steer the
//                                        text in once), or stop (the run is ended).
//   GET  loop-incidents                  people: the loop incidents of runs they may view, newest
//                                        first, with the tokens and cost each burned
//   GET  loop-incidents/{id}             people: one incident
//   POST loop-incidents/{id}/resolve     people who may view its run: retry with a hint, mark it
//                                        reassigned (the task was handed to another seat), or close
//   GET  loop-breaker/settings           managers: the thresholds (platform default, override)
//   PUT  loop-breaker/settings           managers: the company's override (null fields: default)
//   GET  /v1/operator/loop-breaker       platform admins: the platform default
//   PUT  /v1/operator/loop-breaker       platform admins: change the platform default
//
// A run is looping when the same cycle of calls (tool, normalised args and result) repeats
// `repeat_limit` times with no progress in between, the same intent over read-only calls repeats
// `intent_repeat_limit` times, or there is no progress for `stall_minutes` while tokens are spent.
// Progress: a board or task write, a comment or message posted, a file or output produced, a
// changed checkpoint, a new distinct call. Heartbeats and repeats are not. One warning comes first,
// one repeat before the stop.
import { z } from "zod";
import { LoopIncidentKind, LoopIncidentStatus, RunState } from "../enums";
import { MicrosAmount, Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const agents = { feature: "agents" } as const;

export const LOOP_TURNS_MAX = 50;

/** One turn the agent took: a tool call and what came before it. Never the raw args or result. */
export const LoopTurn = z
  .strictObject({
    tool: z.string().trim().min(1).max(200).describe("The tool's name, e.g. `vision_analyze`"),
    args_hash: z
      .string()
      .max(128)
      .optional()
      .describe("A hash of the call's normalised arguments (the caller's choice of hash)"),
    result_hash: z.string().max(128).optional().describe("A hash of the call's result"),
    intent: z
      .string()
      .max(500)
      .optional()
      .describe("What the assistant said it was doing before the call (its first sentence)"),
    read_only: z
      .boolean()
      .optional()
      .describe("The call only read (a search, a file read, vision)"),
    write: z
      .boolean()
      .optional()
      .describe("The call produced something: a file, a message, a board write (progress)"),
    tokens_in: z.int().min(0).max(10_000_000).optional(),
    tokens_out: z.int().min(0).max(10_000_000).optional(),
    model: z.string().min(1).max(200).optional(),
    cost_micros: MicrosAmount.min(0).max(100_000_000).optional(),
  })
  .meta({ id: "LoopTurn" });
export type LoopTurn = z.infer<typeof LoopTurn>;

export const RunTurnsReport = z
  .strictObject({
    turns: z.array(LoopTurn).max(LOOP_TURNS_MAX),
    /** The runtime's own guard ended the run as a loop (`LOOP_STOPPED: <intent>`). */
    guard_stop: z
      .strictObject({
        intent: z.string().trim().min(1).max(500),
        detail: z.string().max(1000).optional(),
      })
      .optional(),
  })
  .meta({ id: "RunTurnsReport" });

export const LoopVerdict = z
  .object({
    /** `warn`: steer `steer` into the running turn once; `stop`: the run was ended. */
    verdict: z.enum(["ok", "warn", "stop"]),
    steer: z.string().nullable(),
    /** The plain reason of a warning or stop ("Stopped: repeating X without progress"). */
    reason: z.string().nullable(),
    run_state: RunState,
    incident_id: Uuid.nullable(),
  })
  .meta({ id: "LoopVerdict" });
export type LoopVerdict = z.infer<typeof LoopVerdict>;

/** The thresholds (effective, the platform default, or a company's override with nulls). */
export const LoopThresholds = z
  .object({
    enabled: z.boolean(),
    repeat_limit: z.int().min(2).max(20),
    intent_repeat_limit: z.int().min(2).max(50),
    stall_minutes: z.int().min(1).max(240),
    similarity_pct: z.int().min(50).max(100),
  })
  .meta({ id: "LoopThresholds" });
export type LoopThresholds = z.infer<typeof LoopThresholds>;

/** A company's override: each field null (or absent) takes the platform default. */
export const LoopThresholdsOverride = z
  .strictObject({
    enabled: z.boolean().nullable().optional(),
    repeat_limit: z.int().min(2).max(20).nullable().optional(),
    intent_repeat_limit: z.int().min(2).max(50).nullable().optional(),
    stall_minutes: z.int().min(1).max(240).nullable().optional(),
    similarity_pct: z.int().min(50).max(100).nullable().optional(),
  })
  .meta({ id: "LoopThresholdsOverride" });
export type LoopThresholdsOverride = z.infer<typeof LoopThresholdsOverride>;

export const LoopBreakerSettings = z
  .object({
    effective: LoopThresholds,
    platform: LoopThresholds,
    override: LoopThresholdsOverride.nullable(),
  })
  .meta({ id: "LoopBreakerSettings" });

export const LoopIncident = z
  .object({
    id: Uuid,
    run_id: Uuid,
    membership_id: Uuid,
    /** The agent's name (a DE's display name). */
    agent_name: z.string(),
    seat_id: Uuid.nullable(),
    task_id: Uuid.nullable(),
    task_title: z.string().nullable(),
    kind: LoopIncidentKind,
    what: z.string(),
    reason: z.string(),
    repeats: z.int().min(0),
    tokens_in: z.int().min(0),
    tokens_out: z.int().min(0),
    cost_micros: MicrosAmount,
    minutes: z.int().min(0),
    warned_at: Timestamp.nullable(),
    detected_at: Timestamp,
    status: LoopIncidentStatus,
    resolved_by_membership_id: Uuid.nullable(),
    resolved_at: Timestamp.nullable(),
    resolution_note: z.string().nullable(),
    reassigned_seat_id: Uuid.nullable(),
  })
  .meta({ id: "LoopIncident" });
export type LoopIncident = z.infer<typeof LoopIncident>;

export const LoopIncidentListQuery = z.object({
  status: LoopIncidentStatus.optional(),
  membership_id: Uuid.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export const LoopIncidentList = z
  .object({
    data: z.array(LoopIncident),
    /** Over the listed rows: what the loops burned. */
    totals: z.object({
      incidents: z.int().min(0),
      open: z.int().min(0),
      tokens: z.int().min(0),
      cost_micros: MicrosAmount,
    }),
  })
  .meta({ id: "LoopIncidentList" });

export const LoopIncidentResolve = z
  .strictObject({
    action: z.enum(["retry", "reassign", "close"]),
    /** `retry`: what to do differently (sent to the agent with the wake-up). */
    hint: z.string().trim().min(1).max(2000).optional(),
    /** `reassign`: the seat the task was handed to (already one of its assignees). */
    seat_id: Uuid.optional(),
  })
  .meta({ id: "LoopIncidentResolve" });

const incidents = `${base}/loop-incidents`;

export const loopBreakerRoutes = {
  "runs.reportTurns": defineRoute({
    ...agents,
    id: "runs.reportTurns",
    method: "post",
    path: `${base}/runs/{id}/turns`,
    tag: "runs",
    summary:
      "LOOP-BREAKER: report the agent's turns (or the runtime guard's stop); the loop verdict",
    access: "run_token",
    params: CompanyItemParams,
    body: RunTurnsReport,
    idempotency: "required",
    response: { status: 200, schema: LoopVerdict },
    errors: ["run_not_owned", "run_state_conflict", "budget_exhausted"],
    emits: [
      "run.step_recorded",
      "run.started",
      "run.loop_warned",
      "run.loop_stopped",
      "run.failed",
      "budget.settled",
    ],
  }),
  "loopIncidents.list": defineRoute({
    ...agents,
    id: "loopIncidents.list",
    method: "get",
    path: incidents,
    tag: "runs",
    summary: "LOOP-BREAKER: loop incidents of the runs the caller may view, newest first",
    access: "member",
    params: CompanyParams,
    query: LoopIncidentListQuery,
    response: { status: 200, schema: LoopIncidentList },
    errors: [],
    emits: [],
  }),
  "loopIncidents.get": defineRoute({
    ...agents,
    id: "loopIncidents.get",
    method: "get",
    path: `${incidents}/{id}`,
    tag: "runs",
    summary: "LOOP-BREAKER: one loop incident",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: LoopIncident },
    errors: [],
    emits: [],
  }),
  "loopIncidents.resolve": defineRoute({
    ...agents,
    id: "loopIncidents.resolve",
    method: "post",
    path: `${incidents}/{id}/resolve`,
    tag: "runs",
    summary: "LOOP-BREAKER: retry with a hint, mark reassigned, or close a loop incident",
    access: "member",
    params: CompanyItemParams,
    body: LoopIncidentResolve,
    idempotency: "optional",
    response: { status: 200, schema: LoopIncident },
    errors: ["conflict"],
    emits: ["loop_incident.resolved", "message.created", "inbox_item.created", "wakeup.requested"],
  }),
  "loopBreaker.settings.get": defineRoute({
    ...agents,
    id: "loopBreaker.settings.get",
    method: "get",
    path: `${base}/loop-breaker/settings`,
    tag: "runs",
    summary: "LOOP-BREAKER: the loop thresholds (effective, platform default, company override)",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: LoopBreakerSettings },
    errors: [],
    emits: [],
  }),
  "loopBreaker.settings.update": defineRoute({
    ...agents,
    id: "loopBreaker.settings.update",
    method: "put",
    path: `${base}/loop-breaker/settings`,
    tag: "runs",
    summary: "LOOP-BREAKER: set the company's override of the loop thresholds",
    access: "manager",
    params: CompanyParams,
    body: LoopThresholdsOverride,
    response: { status: 200, schema: LoopBreakerSettings },
    errors: [],
    emits: ["company.updated"],
  }),
  "operator.loopBreaker.get": defineRoute({
    feature: "core",
    tag: "operator",
    access: "operator",
    id: "operator.loopBreaker.get",
    method: "get",
    path: "/v1/operator/loop-breaker",
    summary: "LOOP-BREAKER: the platform's default loop thresholds",
    response: { status: 200, schema: LoopThresholds },
    errors: [],
    emits: [],
  }),
  "operator.loopBreaker.set": defineRoute({
    feature: "core",
    tag: "operator",
    access: "operator",
    id: "operator.loopBreaker.set",
    method: "put",
    path: "/v1/operator/loop-breaker",
    summary: "LOOP-BREAKER: change the platform's default loop thresholds",
    body: LoopThresholdsOverride,
    response: { status: 200, schema: LoopThresholds },
    errors: [],
    emits: ["loop_breaker.default_changed"],
  }),
} as const;
